import React from 'react';
// Classic PQRST ECG waveform path — repeating pattern
// Each "beat" spans ~200 units wide. We tile multiple beats across a wide SVG.
const SINGLE_BEAT = `l 30 0 l 5 -8 l 5 8 l 10 0 l 3 -4 l 4 4 l 8 0 l 3 -45 l 6 70 l 5 -25 l 4 0 l 6 10 l 8 -10 l 15 0 l 5 6 l 8 -6 l 30 0`;
function buildEcgPath(beats: number): string {
  let path = 'M 0 50';
  for (let i = 0; i < beats; i++) {
    path += ` ${SINGLE_BEAT}`;
  }
  // End with a flat line to fill remaining space
  path += ' l 200 0';
  return path;
}
interface EcgBackgroundProps {
  /** Gold color intensity — 'subtle' (default) or 'medium' */
  intensity?: 'subtle' | 'medium';
  /** Vertical position: 'center' (default), 'top', 'bottom' */
  position?: 'center' | 'top' | 'bottom';
  /** Animation duration in seconds (default 8) */
  speed?: number;
  /** Background color for edge fades (default '#0A0A0A') */
  bgColor?: string;
}
export function EcgBackground({
  intensity = 'subtle',
  position = 'center',
  speed = 8,
  bgColor = '#0A0A0A'
}: EcgBackgroundProps) {
  const lineOpacity = intensity === 'subtle' ? 0.07 : 0.12;
  const glowOpacity = intensity === 'subtle' ? 0.04 : 0.07;
  const beats = 14; // enough to cover wide screens with animation offset
  const positionClass = position === 'top' ? 'top-[15%]' : position === 'bottom' ? 'bottom-[15%]' : 'top-1/2 -translate-y-1/2';
  const ecgPath = buildEcgPath(beats);
  // Total approximate width of the path
  const pathWidth = beats * 155 + 200;
  const dashLength = pathWidth;
  return <div className={`absolute left-0 right-0 ${positionClass} pointer-events-none overflow-hidden`} style={{
    height: '120px'
  }} aria-hidden="true" data-id="element-4">
      {/* Inline keyframes for the ECG trace animation */}
      <style data-id="element-5">{`
        @keyframes ecg-trace-${speed} {
          0% {
            stroke-dashoffset: ${dashLength};
          }
          100% {
            stroke-dashoffset: 0;
          }
        }
        @keyframes ecg-scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>

      {/* Scrolling container — two copies for seamless loop */}
      <div className="flex" style={{
      width: `${pathWidth * 2}px`,
      animation: `ecg-scroll ${speed * 2}s linear infinite`
    }} data-id="element-6">
        {[0, 1].map(copy => <svg key={copy} viewBox={`0 0 ${pathWidth} 100`} preserveAspectRatio="none" className="flex-shrink-0" style={{
        width: `${pathWidth}px`,
        height: '120px'
      }} data-id="element-7">
            {/* Glow layer — thicker, blurred */}
            <path d={ecgPath} fill="none" stroke="#D4A843" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" opacity={glowOpacity} style={{
          filter: 'blur(8px)'
        }} data-id="element-8" />
            {/* Main ECG line */}
            <path d={ecgPath} fill="none" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity={lineOpacity} data-id="element-9" />
            {/* Bright trace line — animated */}
            <path d={ecgPath} fill="none" stroke="#D4A843" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity={lineOpacity * 2.5} strokeDasharray={`80 ${dashLength - 80}`} style={{
          animation: `ecg-trace-${speed} ${speed}s linear infinite`
        }} data-id="element-10" />
          </svg>)}
      </div>

      {/* Edge fade — left */}
      <div className="absolute left-0 top-0 bottom-0 w-24 md:w-40 z-10" style={{
      background: `linear-gradient(to right, ${bgColor}, transparent)`
    }} data-id="element-11" />
      {/* Edge fade — right */}
      <div className="absolute right-0 top-0 bottom-0 w-24 md:w-40 z-10" style={{
      background: `linear-gradient(to left, ${bgColor}, transparent)`
    }} data-id="element-12" />
    </div>;
}